import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Redirect, Route, Switch, useHistory } from '@treams/treams-routes';
import { Breakpoint, Button, Color, LanguageSelect, StatusBar, Title } from '@treams/treams-ui';
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	ExpiredIllustration,
	LockIcon,
} from '@treams/treams-icons';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import ProgressBar from './ProgressBar';

import Face from './questions/Face';
import PickOne from './questions/PickOne';
import OpenQuestion from './questions/OpenQuestion';
import Menu from '../navigation/Menu';
import EngagementThankYou from './EngagementThankYou';
import Enps from './questions/Enps';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';

type RouteParams = {
	question: string;
};

const Container = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
	min-height: 100vh;
`;

function EngagementQuestion(props: {
	response: graphql.EngagementResponse;
	question: graphql.EngagementQuestion;
	answer?: graphql.EngagementAnswer;
	secret?: string;
}) {
	const history = useHistory();
	const { localize } = useLocalize();
	const [update] = graphql.useUpdateEngagementAnswerMutation();
	const formik = useFormik({
		initialValues: {
			answer: props.answer
				? {
						index: props.answer.index,
						text: props.answer.text,
				  }
				: undefined,
		},
		onSubmit: async (values) => {
			await update({
				variables: {
					input: {
						responseID: props.response.id,
						secret: props.secret ?? null,
						questionID: props.question.id,
						skipped: !values.answer,
						index: values.answer?.index,
						text: values.answer?.text,
					},
				},
			});

			history.push(
				'/engagement/' + props.response.id + (props.secret ? '?secret=' + props.secret : ''),
			);
		},
	});

	return (
		<Flex flexDirection="column" alignItems="center" flexGrow={1} maxWidth={670}>
			<Title textAlign="center" mb={4}>
				{localize(props.question.title).text}
			</Title>
			<FormikProvider value={formik}>
				{props.question.type.type === graphql.EngagementResponseType.PickOne && (
					<PickOne type={props.question.type} />
				)}
				{props.question.type.type === graphql.EngagementResponseType.Face && (
					<Face
						type={props.question.type}
						answerTitles={props.question.type.options?.map((option) => option.title!)}
					/>
				)}
				{props.question.type.type === graphql.EngagementResponseType.Open && (
					<OpenQuestion type={props.question.type} />
				)}
				{props.question.type.type === graphql.EngagementResponseType.Enps && (
					<Enps
						type={props.question.type}
						answerTitles={props.question.type.options?.map((option) => option.title!)}
					/>
				)}
			</FormikProvider>
		</Flex>
	);
}

function EngagementResponseRedirect(props: {
	response: graphql.EngagementResponse;
	secret?: string;
}) {
	const question = props.response.request.questions.find(
		(question) => !props.response.answers.some((answer) => question.id === answer.questionID),
	);

	if (!question) {
		return <EngagementThankYou />;
	}

	return (
		<Redirect
			to={`/engagement/${props.response.id}/${question.id}${
				props.secret ? '?secret=' + props.secret : ''
			}`}
		/>
	);
}

function EngagementNavigationButtons(props: {
	responseID: string;
	questionID: string;
	previousQuestionID?: string;
	secret?: string;
}) {
	const { t } = useTranslation();
	const history = useHistory();
	const [update] = graphql.useUpdateEngagementAnswerMutation();

	return (
		<Flex width="100%" justifyContent="flex-end" mb={3}>
			{props.previousQuestionID && (
				<Box flexGrow={0} flexShrink={0}>
					<Button
						title={t('engagement.respond.back')}
						type="secondary"
						small
						icon={<ChevronLeftIcon />}
						onClick={() => {
							history.push(
								'/engagement/' +
									props.responseID +
									'/' +
									props.previousQuestionID! +
									(props.secret ? '?secret=' + props.secret : ''),
							);
						}}
					/>
				</Box>
			)}
			<Box flexGrow={1} flexShrink={1} />
			<Box flexGrow={0} flexShrink={0}>
				<Color color="slateBlue">
					<Button
						title={t('engagement.respond.skip')}
						type="plain"
						small
						rightIcon={<ChevronRightIcon />}
						onClick={async () => {
							await update({
								variables: {
									input: {
										responseID: props.responseID,
										secret: props.secret ?? null,
										questionID: props.questionID,
										skipped: true,
										index: undefined,
										text: undefined,
									},
								},
							});

							history.push(
								'/engagement/' + props.responseID + (props.secret ? '?secret=' + props.secret : ''),
							);
						}}
					/>
				</Color>
			</Box>
		</Flex>
	);
}

export default function EngagementResponse(props: { id: string; secret?: string }) {
	const { t, i18n } = useTranslation();
	const workspace = useWorkspace();

	const engagementQuery = graphql.useEngagementResponseQuery({
		variables: {
			id: props.id,
			secret: props.secret ?? null,
		},
	});

	const availableLanguageQuery = graphql.useSupportedLanguagesInWorkspaceQuery({
		variables: {
			workspaceID: workspace.id,
		},
	});

	if (!engagementQuery.data || !availableLanguageQuery.data) {
		return null;
	}

	const engagementResponse = engagementQuery.data.engagementResponse;
	const supportedLocales = availableLanguageQuery.data.workspace.supportedLanguageIDs;

	const onLocaleChange = (locale: string) => {
		const language = locale.split('_')[0];
		i18n.changeLanguage(language);
	};

	const currentLanguage =
		supportedLocales.find((locale) => locale.split('_')[0] === i18n.language) ||
		supportedLocales[0];

	return (
		<Container>
			<StyledStatusBox display="flex">
				<StatusBar
					icon={<LockIcon />}
					text={
						<>
							{t('engagement.respond.anonymous.title.0')}{' '}
							<strong>{t('engagement.respond.anonymous.title.1')}</strong>.
						</>
					}
				/>
				<StyledLanguageBox>
					<LanguageSelect value={currentLanguage} onChange={onLocaleChange} />
				</StyledLanguageBox>
			</StyledStatusBox>
			<Menu plain />
			<Flex p={3} flexDirection="column" alignItems="center">
				{engagementResponse.isExpired && (
					<Flex flexDirection="column" alignItems="center" flexGrow={1} maxWidth={670} mt={5}>
						<Title textAlign="center" mb={4}>
							{t('engagement.respond.expired')}
						</Title>

						<img height="300" src={ExpiredIllustration} alt="" />
					</Flex>
				)}
				{!engagementResponse.isExpired && (
					<Switch>
						<Route path={`/engagement/:id`} exact>
							<EngagementResponseRedirect
								response={engagementResponse as graphql.EngagementResponse}
								secret={props.secret}
							/>
						</Route>
						<Route<RouteParams> path={`/engagement/:id/:question`} exact>
							{({ match }) => (
								<>
									<EngagementNavigationButtons
										responseID={engagementResponse.id}
										secret={props.secret}
										questionID={match!.params.question!}
										previousQuestionID={
											engagementResponse.request.questions[
												engagementResponse.request.questions.findIndex(
													(question) => question.id === match!.params.question,
												) - 1
											]?.id
										}
									/>
									<Flex mt={0} width="100%" justifyContent="center">
										<EngagementQuestion
											key={match!.params.question}
											response={engagementResponse as graphql.EngagementResponse}
											secret={props.secret}
											question={
												engagementResponse.request.questions.find(
													(question) => question.id === match!.params.question,
												) as graphql.EngagementQuestion
											}
											answer={
												engagementResponse.answers.find(
													(answer) => answer.questionID === match!.params.question,
												) as graphql.EngagementAnswer
											}
										/>
									</Flex>
									<Box mt={5} mb={5}>
										<ProgressBar
											selected={match!.params.question}
											response={engagementResponse as graphql.EngagementResponse}
											secret={props.secret}
										/>
									</Box>
								</>
							)}
						</Route>
					</Switch>
				)}
			</Flex>
		</Container>
	);
}

const StyledStatusBox = styled(Box)`
	flex-direction: column;

	@media (min-width: ${Breakpoint.Small}) {
		flex-direction: row;
	}
`;

const StyledLanguageBox = styled(Box)`
	flex-grow: 1;

	div {
		border-radius: 0 !important;
	}

	@media (min-width: ${Breakpoint.Small}) {
		right: 0;
		position: absolute;
	}
`;
